import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import DashboardLayout from '../components/DashboardLayout';
import { Upload as UploadIcon, Image, Music2, Calendar, Loader2, Info, X, AlertCircle, Users, Globe, Tag, Plus, Minus } from 'lucide-react';

const genres = [
  'Pop', 'Rock', 'Hip Hop', 'R&B', 'Jazz', 'Electronic', 'Classical', 'Country',
  'Folk', 'Latin', 'Metal', 'Blues', 'Reggae', 'World', 'Alternative', 'Indie'
].sort();

const languages = [
  'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Japanese',
  'Korean', 'Chinese', 'Arabic', 'Hindi', 'Russian', 'Instrumental'
].sort();

interface Track {
  title: string;
  audioFile: File | null;
  isrc: string;
}

const UploadForm = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: 'single' | 'album' }>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    artistName: '',
    releaseDate: '',
    language: '',
    genre: '',
    writers: '',
    producers: '',
    label: '',
    publisher: '',
    copyrightHolder: '',
    year: new Date().getFullYear().toString(),
  });
  const [tracks, setTracks] = useState<Track[]>([{ title: '', audioFile: null, isrc: '' }]);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const minReleaseDate = new Date();
  minReleaseDate.setDate(minReleaseDate.getDate() + 2);
  const minDateString = minReleaseDate.toISOString().split('T')[0];

  const handleCoverChange = (file: File) => {
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setCoverPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleTrackChange = (index: number, field: keyof Track, value: any) => {
    const newTracks = [...tracks];
    newTracks[index] = { ...newTracks[index], [field]: value };
    setTracks(newTracks);
  };

  const addTrack = () => {
    setTracks([...tracks, { title: '', audioFile: null, isrc: '' }]);
  };

  const removeTrack = (index: number) => {
    if (tracks.length > 1) {
      const newTracks = tracks.filter((_, i) => i !== index);
      setTracks(newTracks);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.writers.trim()) {
        throw new Error('Please enter songwriter information');
      }
      if (!formData.producers.trim()) {
        throw new Error('Please enter producer information');
      }
      if (!formData.copyrightHolder.trim()) {
        throw new Error('Please enter copyright holder information');
      }

      const updatedFormData = {
        ...formData,
        publisher: formData.copyrightHolder
      };

      if (!coverFile) {
        throw new Error('Please upload cover art');
      }

      if (tracks.some(track => !track.audioFile || !track.title)) {
        throw new Error('Please provide all track information');
      }

      const coverPath = `covers/${Date.now()}-${coverFile.name}`;
      const { error: coverError } = await supabase.storage
        .from('media')
        .upload(coverPath, coverFile);
      if (coverError) throw coverError;

      const coverUrl = supabase.storage.from('media').getPublicUrl(coverPath).data.publicUrl;

      const { data: release, error: releaseError } = await supabase
        .from('releases')
        .insert({
          title: updatedFormData.title,
          artist_name: updatedFormData.artistName,
          release_date: updatedFormData.releaseDate,
          cover_url: coverUrl,
          status: 'draft',
          type: type,
          track_count: tracks.length,
          language: updatedFormData.language,
          genre: updatedFormData.genre,
          writers: updatedFormData.writers,
          producers: updatedFormData.producers,
          label: updatedFormData.label,
          publisher: updatedFormData.publisher,
          copyright_holder: updatedFormData.copyrightHolder,
          copyright_year: updatedFormData.year,
        })
        .select()
        .single();

      if (releaseError) throw releaseError;

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        const audioPath = `audio/${Date.now()}-${track.audioFile!.name}`;
        
        const { error: audioError } = await supabase.storage
          .from('media')
          .upload(audioPath, track.audioFile!);
        if (audioError) throw audioError;

        const audioUrl = supabase.storage.from('media').getPublicUrl(audioPath).data.publicUrl;

        const { error: trackError } = await supabase
          .from('tracks')
          .insert({
            release_id: release.id,
            title: track.title,
            track_number: i + 1,
            audio_url: audioUrl,
            isrc: track.isrc,
          });

        if (trackError) throw trackError;
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Upload {type === 'single' ? 'Single' : 'EP/Album'}
          </h1>
          <p className="text-gray-300">Fill in the details below to upload your release.</p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-400 p-4 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-4">
                Cover Art
                <div className="text-xs text-gray-400 mt-1">
                  Recommended: 3000x3000px square PNG
                </div>
              </label>
              <div className="relative">
                {coverPreview ? (
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10">
                    <img
                      src={coverPreview}
                      alt="Cover Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setCoverFile(null);
                        setCoverPreview(null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <FileUpload
                    icon={<Image className="h-6 w-6" />}
                    label="Upload Cover Art"
                    accept="image/*"
                    onChange={handleCoverChange}
                    className="aspect-square"
                  />
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  {type === 'single' ? 'Track Title' : 'Release Title'}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                  required
                  placeholder={`Enter ${type === 'single' ? 'track' : 'release'} title`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Artist Name
                </label>
                <input
                  type="text"
                  value={formData.artistName}
                  onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                  className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                  required
                  placeholder="Enter artist name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Release Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-400" />
                  <input
                    type="date"
                    value={formData.releaseDate}
                    onChange={(e) => setFormData({ ...formData, releaseDate: e.target.value })}
                    min={minDateString}
                    className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 pl-10 pr-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-gray-400 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Release date must be at least 2 days from today
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Language
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    required
                  >
                    <option value="">Select Language</option>
                    {languages.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Genre
                  </label>
                  <select
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    required
                  >
                    <option value="">Select Genre</option>
                    {genres.map((genre) => (
                      <option key={genre} value={genre}>{genre}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Tracks</h3>
              {type === 'album' && (
                <button
                  type="button"
                  onClick={addTrack}
                  className="flex items-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 transition-colors px-4 py-2 rounded-lg"
                >
                  <Plus className="h-4 w-4" />
                  Add Track
                </button>
              )}
            </div>

            <div className="space-y-4">
              {tracks.map((track, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Track {index + 1}</h4>
                    {type === 'album' && tracks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTrack(index)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-2">
                        Track Title
                      </label>
                      <input
                        type="text"
                        value={track.title}
                        onChange={(e) => handleTrackChange(index, 'title', e.target.value)}
                        className="w-full bg-black/20 rounded-lg border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                        required
                        placeholder="Enter track title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-2">
                        ISRC Code
                      </label>
                      <input
                        type="text"
                        value={track.isrc}
                        onChange={(e) => handleTrackChange(index, 'isrc', e.target.value)}
                        className="w-full bg-black/20 rounded-lg border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder="Enter ISRC code (if available)"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      Audio File
                    </label>
                    <FileUpload
                      icon={<Music2 className="h-6 w-6" />}
                      label="Upload Audio"
                      accept="audio/*"
                      onChange={(file) => handleTrackChange(index, 'audioFile', file)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Songwriters *
              </label>
              <input
                type="text"
                value={formData.writers}
                onChange={(e) => setFormData({ ...formData, writers: e.target.value })}
                className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Enter songwriter names (comma separated)"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Producers *
              </label>
              <input
                type="text"
                value={formData.producers}
                onChange={(e) => setFormData({ ...formData, producers: e.target.value })}
                className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Enter producer names (comma separated)"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Record Label
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Enter record label name (if applicable)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Copyright Holder *
              </label>
              <input
                type="text"
                value={formData.copyrightHolder}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    copyrightHolder: e.target.value,
                    publisher: e.target.value
                  });
                }}
                className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Enter copyright holder"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Copyright Year *
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                className="w-full bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                min="1900"
                max={new Date().getFullYear()}
                required
              />
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Info className="h-5 w-5 text-purple-400" />
              Release Guidelines
            </h3>
            <ul className="text-sm text-gray-300 space-y-2">
              <li>• Cover art should not contain URLs or store logos</li>
              <li>• Audio quality should meet professional standards</li>
              <li>• Ensure you have rights to all content</li>
              <li>• Release date allows time for store processing</li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-4 rounded-xl text-white font-medium hover:from-purple-600 hover:to-purple-800 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 group"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <UploadIcon className="h-5 w-5" />
                Upload Release
                <div className="w-5 h-5 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors" />
              </>
            )}
          </button>
        </form>
      </div>
    </DashboardLayout>
  );
};

const FileUpload = ({
  icon,
  label,
  accept,
  onChange,
  className = '',
}: {
  icon: React.ReactNode;
  label: string;
  accept: string;
  onChange: (file: File) => void;
  className?: string;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      onChange(selectedFile);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith(accept.replace('/*', ''))) {
      setFile(droppedFile);
      onChange(droppedFile);
    }
  };

  return (
    <div className="relative">
      <input
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        id={`file-${label}`}
      />
      <label
        htmlFor={`file-${label}`}
        className={`flex flex-col items-center justify-center gap-3 p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
          isDragging
            ? 'border-purple-500 bg-purple-500/20'
            : file
            ? 'border-purple-500 bg-purple-500/10'
            : 'border-white/20 hover:border-purple-500/50 hover:bg-white/5'
        } ${className}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="bg-purple-500/20 p-3 rounded-lg">
          {icon}
        </div>
        <div className="text-center">
          <span className="text-sm font-medium block">
            {file ? file.name : `Drop your ${label.toLowerCase()} here`}
          </span>
          <span className="text-xs text-gray-400 block mt-1">
            or click to browse
          </span>
        </div>
      </label>
    </div>
  );
};

export default UploadForm;